/**
 * Personal Daily Report Generator
 *
 * This is the core of the personalized cosmic 8-ball.
 * It generates a daily report based on YOUR natal chart and current transits.
 * Questions are then scored against this report.
 */

import { QuestionCategory } from '../types/astrology';
import {
  Transit,
  Planet,
  AspectType,
  UserProfile
} from '../types/userProfile';
import {
  calculateCurrentPositions,
  calculateTransits,
  getMoonPhase,
  getDayRuler,
  isRetrograde,
  scanTransitTiming,
} from './ephemerisService';

export interface CategoryScore {
  score: number; // 1-10
  reasoning: string[];
  advice: string;
  goodFor: string[];
  badFor: string[];
}

export interface PersonalDailyReport {
  date: Date;
  userId: string;

  // Overall energy
  overallScore: number; // 1-10
  overallEnergy: 'excellent' | 'good' | 'mixed' | 'challenging' | 'difficult';
  headline: string;

  // Category-specific scores
  categories: {
    love: CategoryScore;
    career: CategoryScore;
    money: CategoryScore;
    health: CategoryScore;
    social: CategoryScore;
    decisions: CategoryScore;
    creativity: CategoryScore;
    spiritual: CategoryScore;
  };

  // Key transits affecting you today
  keyTransits: {
    transit: Transit;
    interpretation: string;
    affectedCategories: QuestionCategory[];
    impact: 'positive' | 'negative' | 'neutral';
    timing?: import('../types/userProfile').TransitTiming;
  }[];

  // General guidance
  moonPhase: {
    name: string;
    advice: string;
  };

  // Planetary retrogrades affecting you
  retrogrades: {
    planet: Planet;
    inNatalHouse?: string;
    advice: string;
  }[];

  // Cached timestamp
  generatedAt: Date;
}

/**
 * House meanings — what life area each house governs
 */
export const HOUSE_MEANINGS: Record<number, string> = {
  1: 'self and identity',
  2: 'money and possessions',
  3: 'communication and siblings',
  4: 'home and family',
  5: 'creativity and romance',
  6: 'health and daily work',
  7: 'partnerships and marriage',
  8: 'transformation and shared resources',
  9: 'philosophy and travel',
  10: 'career and public image',
  11: 'friends and aspirations',
  12: 'the unconscious and solitude',
};

/**
 * Planet influences on different categories
 */
const PLANET_CATEGORY_INFLUENCE: Record<Planet, QuestionCategory[]> = {
  sun: ['career', 'decisions', 'health'],
  moon: ['love', 'health', 'spiritual'],
  mercury: ['career', 'decisions', 'social'],
  venus: ['love', 'money', 'creativity', 'social'],
  mars: ['career', 'health', 'decisions'],
  jupiter: ['money', 'career', 'spiritual'],
  saturn: ['career', 'money', 'health'],
  uranus: ['decisions', 'creativity', 'social'],
  neptune: ['spiritual', 'creativity', 'love'],
  pluto: ['decisions', 'spiritual', 'career'],
  ascendant: ['social', 'health'],
  midheaven: ['career'],
  northNode: ['spiritual', 'career'],
  chiron: ['health', 'spiritual']
};

/**
 * Aspect type impacts
 */
const ASPECT_IMPACTS: Record<AspectType, { score: number; nature: 'positive' | 'negative' | 'neutral' }> = {
  conjunction: { score: 0, nature: 'neutral' }, // Can be positive or negative depending on planets
  trine: { score: 2, nature: 'positive' },
  sextile: { score: 1.5, nature: 'positive' },   // Slightly buffed — opportunities should register
  square: { score: -1.5, nature: 'negative' },    // Rebalanced: tension, not devastation
  opposition: { score: -1, nature: 'negative' },  // Rebalanced: polarity, but manageable
  quincunx: { score: -0.5, nature: 'negative' }   // Mild discomfort, not real penalty
};

/**
 * Benefic vs Malefic planets
 */
const BENEFIC_PLANETS: Planet[] = ['venus', 'jupiter', 'sun'];
const MALEFIC_PLANETS: Planet[] = ['mars', 'saturn', 'pluto'];

/**
 * Get conjunction impact based on planets involved
 */
function getConjunctionImpact(planet1: Planet, planet2: Planet): number {
  const isBenefic1 = BENEFIC_PLANETS.includes(planet1);
  const isBenefic2 = BENEFIC_PLANETS.includes(planet2);
  const isMalefic1 = MALEFIC_PLANETS.includes(planet1);
  const isMalefic2 = MALEFIC_PLANETS.includes(planet2);

  if (isBenefic1 && isBenefic2) return 2;
  if (isMalefic1 && isMalefic2) return -2;
  if ((isBenefic1 && isMalefic2) || (isMalefic1 && isBenefic2)) return -1;
  return 0;
}

/**
 * Interpret a transit for the report
 */
function interpretTransit(transit: Transit): string {
  const planetNames: Record<Planet, string> = {
    sun: 'Sun', moon: 'Moon', mercury: 'Mercury', venus: 'Venus',
    mars: 'Mars', jupiter: 'Jupiter', saturn: 'Saturn', uranus: 'Uranus',
    neptune: 'Neptune', pluto: 'Pluto', ascendant: 'Ascendant',
    midheaven: 'Midheaven', northNode: 'North Node', chiron: 'Chiron'
  };

  const aspectVerbs: Record<AspectType, string> = {
    conjunction: 'merges with',
    opposition: 'opposes',
    trine: 'harmonizes with',
    square: 'challenges',
    sextile: 'supports',
    quincunx: 'creates tension with'
  };

  const tp = planetNames[transit.transitPlanet];
  const np = planetNames[transit.natalPlanet];
  const verb = aspectVerbs[transit.aspectType];

  let text = `Transit ${tp} ${verb} your natal ${np}`;

  // Add house context when available
  if (transit.transitHouse) {
    const houseMeaning = HOUSE_MEANINGS[transit.transitHouse];
    if (houseMeaning) {
      text += ` in your ${ordinal(transit.transitHouse)} house of ${houseMeaning}`;
    }
  }

  return text;
}

/**
 * Zodiac sign element and modality
 */
type ZodiacSign = 'Aries' | 'Taurus' | 'Gemini' | 'Cancer' | 'Leo' | 'Virgo' |
                  'Libra' | 'Scorpio' | 'Sagittarius' | 'Capricorn' | 'Aquarius' | 'Pisces';

const SIGN_ELEMENTS: Record<ZodiacSign, 'fire' | 'earth' | 'air' | 'water'> = {
  Aries: 'fire', Leo: 'fire', Sagittarius: 'fire',
  Taurus: 'earth', Virgo: 'earth', Capricorn: 'earth',
  Gemini: 'air', Libra: 'air', Aquarius: 'air',
  Cancer: 'water', Scorpio: 'water', Pisces: 'water'
};

/**
 * Natal placement modifiers for categories
 * These adjust the BASE score based on where your planets naturally sit
 *
 * For example: Venus in Pisces = naturally dreamy/idealistic about money
 * This means you need HIGHER transit support to take financial risks
 */
interface NatalModifier {
  planet: Planet;
  sign: ZodiacSign;
  categoryModifiers: Partial<Record<QuestionCategory, number>>;
  warnings: Partial<Record<QuestionCategory, string>>;
}

/**
 * Essential dignity table — based on classical astrology.
 * Domicile and Exaltation boost; Detriment and Fall penalize.
 * This replaces the scattered if/else approach with a systematic lookup.
 */
type Dignity = 'domicile' | 'exaltation' | 'detriment' | 'fall';
const ESSENTIAL_DIGNITIES: Partial<Record<string, { dignity: Dignity; mod: number }>> = {
  // Sun
  'sun:Leo':             { dignity: 'domicile',   mod:  1 },
  'sun:Aries':           { dignity: 'exaltation', mod:  0.5 },
  'sun:Aquarius':        { dignity: 'detriment',  mod: -1 },
  'sun:Libra':           { dignity: 'fall',       mod: -0.5 },
  // Moon
  'moon:Cancer':         { dignity: 'domicile',   mod:  1 },
  'moon:Taurus':         { dignity: 'exaltation', mod:  0.5 },
  'moon:Capricorn':      { dignity: 'detriment',  mod: -1 },
  'moon:Scorpio':        { dignity: 'fall',       mod: -0.5 },
  // Mercury
  'mercury:Gemini':      { dignity: 'domicile',   mod:  1 },
  'mercury:Virgo':       { dignity: 'domicile',   mod:  1 },
  'mercury:Sagittarius': { dignity: 'detriment',  mod: -1 },
  'mercury:Pisces':      { dignity: 'detriment',  mod: -1 },
  // Venus
  'venus:Taurus':        { dignity: 'domicile',   mod:  1 },
  'venus:Libra':         { dignity: 'domicile',   mod:  1 },
  'venus:Pisces':        { dignity: 'exaltation', mod:  0.5 },
  'venus:Scorpio':       { dignity: 'detriment',  mod: -1 },
  'venus:Aries':         { dignity: 'detriment',  mod: -1 },
  'venus:Virgo':         { dignity: 'fall',       mod: -0.5 },
  // Mars
  'mars:Aries':          { dignity: 'domicile',   mod:  1 },
  'mars:Scorpio':        { dignity: 'domicile',   mod:  1 },
  'mars:Capricorn':      { dignity: 'exaltation', mod:  0.5 },
  'mars:Libra':          { dignity: 'detriment',  mod: -1 },
  'mars:Taurus':         { dignity: 'detriment',  mod: -1 },
  'mars:Cancer':         { dignity: 'fall',       mod: -0.5 },
  // Jupiter
  'jupiter:Sagittarius': { dignity: 'domicile',   mod:  1 },
  'jupiter:Pisces':      { dignity: 'domicile',   mod:  1 },
  'jupiter:Cancer':      { dignity: 'exaltation', mod:  0.5 },
  'jupiter:Gemini':      { dignity: 'detriment',  mod: -1 },
  'jupiter:Virgo':       { dignity: 'detriment',  mod: -1 },
  'jupiter:Capricorn':   { dignity: 'fall',       mod: -0.5 },
  // Saturn
  'saturn:Capricorn':    { dignity: 'domicile',   mod:  1 },
  'saturn:Aquarius':     { dignity: 'domicile',   mod:  1 },
  'saturn:Libra':        { dignity: 'exaltation', mod:  0.5 },
  'saturn:Aries':        { dignity: 'detriment',  mod: -1 },
  'saturn:Cancer':       { dignity: 'detriment',  mod: -1 },
  'saturn:Leo':          { dignity: 'fall',       mod: -0.5 },
};

/**
 * Maps planets to the categories their dignity affects.
 * When a planet is dignified/debilitated, these categories shift.
 */
const DIGNITY_CATEGORY_MAP: Record<string, Partial<Record<QuestionCategory, number>>> = {
  sun:     { career: 1, creativity: 0.5, health: 0.5 },
  moon:    { love: 1, health: 0.5, spiritual: 0.5 },
  mercury: { decisions: 1, social: 0.5, career: 0.5 },
  venus:   { love: 1, money: 0.5, social: 0.5 },
  mars:    { career: 0.5, decisions: 1, health: 0.5 },
  jupiter: { money: 1, spiritual: 0.5, career: 0.5 },
  saturn:  { career: 1, decisions: 0.5 },
};

function getNatalModifiers(natalChart: UserProfile['natalChart']): NatalModifier[] {
  const modifiers: NatalModifier[] = [];

  const planetEntries: [string, { sign: ZodiacSign }][] = [
    ['sun', natalChart.sun],
    ['moon', natalChart.moon],
    ['mercury', natalChart.mercury],
    ['venus', natalChart.venus],
    ['mars', natalChart.mars],
    ['jupiter', natalChart.jupiter],
    ['saturn', natalChart.saturn],
    ['uranus', natalChart.uranus],
    ['neptune', natalChart.neptune],
    ['pluto', natalChart.pluto]
  ];

  for (const [planet, data] of planetEntries) {
    if (!data) continue;
    const sign = data.sign as ZodiacSign;

    // ── Essential dignity modifiers (systematic) ──
    const dignityKey = `${planet}:${sign}`;
    const dignity = ESSENTIAL_DIGNITIES[dignityKey];
    const categoryWeights = DIGNITY_CATEGORY_MAP[planet];

    if (dignity && categoryWeights) {
      const categoryModifiers: Partial<Record<QuestionCategory, number>> = {};
      const warnings: Partial<Record<QuestionCategory, string>> = {};
      const direction = dignity.mod > 0 ? 1 : -1;

      for (const [cat, weight] of Object.entries(categoryWeights)) {
        const impact = direction * (weight as number) * Math.abs(dignity.mod);
        categoryModifiers[cat as QuestionCategory] = Math.round(impact * 10) / 10;
      }

      // Warnings only for debilitated placements
      if (dignity.dignity === 'detriment' || dignity.dignity === 'fall') {
        const primaryCat = Object.entries(categoryWeights)
          .sort(([, a], [, b]) => (b as number) - (a as number))[0]?.[0] as QuestionCategory;
        if (primaryCat) {
          const dignityLabel = dignity.dignity === 'detriment' ? 'in detriment' : 'in fall';
          warnings[primaryCat] = `Your ${sign} ${planet.charAt(0).toUpperCase() + planet.slice(1)} is ${dignityLabel} — temper expectations here`;
        }
      }

      modifiers.push({ planet: planet as Planet, sign, categoryModifiers, warnings });
    }

    // ── Specific behavioral modifiers (supplement dignities) ──
    if (planet === 'venus' && sign === 'Pisces') {
      // Venus exalted in Pisces: great for love, risky for money
      modifiers.push({
        planet: planet as Planet, sign,
        categoryModifiers: { money: -0.5 },
        warnings: { money: 'Your Pisces Venus has a dreamy relationship with money — stay grounded' }
      });
    }

    if (planet === 'mercury' && sign === 'Sagittarius') {
      modifiers.push({
        planet: planet as Planet, sign,
        categoryModifiers: { social: -0.5 },
        warnings: { social: 'Your Sagittarius Mercury can be blunt in communication' }
      });
    }

    // Outer planets — element-based modifiers (no traditional dignities)
    if (planet === 'uranus') {
      const element = SIGN_ELEMENTS[sign];
      if (element === 'air') {
        modifiers.push({ planet: planet as Planet, sign, categoryModifiers: { creativity: 0.5, social: 0.5 }, warnings: {} });
      } else if (element === 'earth') {
        modifiers.push({ planet: planet as Planet, sign, categoryModifiers: { creativity: -0.5 }, warnings: {} });
      }
    }
    if (planet === 'neptune') {
      const element = SIGN_ELEMENTS[sign];
      if (element === 'water') {
        modifiers.push({ planet: planet as Planet, sign, categoryModifiers: { spiritual: 0.5, creativity: 0.5 }, warnings: {} });
      } else if (element === 'earth') {
        modifiers.push({ planet: planet as Planet, sign, categoryModifiers: { spiritual: -0.5 }, warnings: {} });
      }
    }
    if (planet === 'pluto') {
      const element = SIGN_ELEMENTS[sign];
      if (element === 'water') {
        modifiers.push({ planet: planet as Planet, sign, categoryModifiers: { spiritual: 0.5 }, warnings: {} });
      }
    }
  }

  return modifiers;
}

/**
 * Generate category score based on relevant transits AND natal placements
 */
function generateCategoryScore(
  category: QuestionCategory,
  transits: Transit[],
  moonPhase: { phase: number; phaseName: string },
  natalModifiers?: NatalModifier[]
): CategoryScore {
  let score = 5.5; // Slightly warm baseline — life isn't usually terrible
  const reasoning: string[] = [];
  const goodFor: string[] = [];
  const badFor: string[] = [];

  // FIRST: Apply natal modifiers (your permanent tendencies)
  if (natalModifiers) {
    for (const mod of natalModifiers) {
      const categoryMod = mod.categoryModifiers[category];
      if (categoryMod !== undefined) {
        score += categoryMod;
        if (mod.warnings[category]) {
          reasoning.push(mod.warnings[category]!);
        }
        if (categoryMod < 0) {
          badFor.push('risky moves', 'impulsive decisions');
        }
      }
    }
  }

  // Find transits affecting this category — with diminishing returns
  let positiveHits = 0;
  let negativeHits = 0;

  for (const transit of transits) {
    const transitInfluences = PLANET_CATEGORY_INFLUENCE[transit.transitPlanet] || [];
    const natalInfluences = PLANET_CATEGORY_INFLUENCE[transit.natalPlanet] || [];

    if (transitInfluences.includes(category) || natalInfluences.includes(category)) {
      let impact: number;

      if (transit.aspectType === 'conjunction') {
        impact = getConjunctionImpact(transit.transitPlanet, transit.natalPlanet);
      } else {
        impact = ASPECT_IMPACTS[transit.aspectType].score;
      }

      // Tighter orbs have stronger effects (clamped to prevent negative)
      const orbMultiplier = transit.isExact ? 1.3 : Math.max(0, 1 - transit.orb / 8);

      // Diminishing returns: each successive hit in the same direction has less impact
      // 1st hit = 100%, 2nd = 70%, 3rd = 50%, 4th+ = 35%
      let diminisher: number;
      if (impact < 0) {
        negativeHits++;
        diminisher = negativeHits === 1 ? 1.0 : negativeHits === 2 ? 0.7 : negativeHits === 3 ? 0.5 : 0.35;
      } else if (impact > 0) {
        positiveHits++;
        diminisher = positiveHits === 1 ? 1.0 : positiveHits === 2 ? 0.7 : positiveHits === 3 ? 0.5 : 0.35;
      } else {
        diminisher = 1.0;
      }

      score += impact * orbMultiplier * diminisher;

      const interpretation = interpretTransit(transit);
      reasoning.push(`${interpretation} (orb: ${transit.orb.toFixed(1)}°)`);
    }
  }

  // Moon phase influence — affects more than just decisions/creativity
  if (moonPhase.phaseName === 'New Moon') {
    if (category === 'decisions' || category === 'creativity') {
      score += 1;
      reasoning.push('New Moon favors new beginnings');
      goodFor.push('starting projects');
    }
    if (category === 'spiritual') {
      score += 0.5;
      reasoning.push('New Moon deepens introspection');
    }
  } else if (moonPhase.phaseName === 'Full Moon') {
    if (category === 'decisions' || category === 'creativity') {
      score += 0.5;
      reasoning.push('Full Moon brings clarity but high emotions');
      goodFor.push('completion', 'celebration');
      badFor.push('starting new things');
    }
    if (category === 'love') {
      score += 0.5;
      reasoning.push('Full Moon heightens emotional connections');
    }
    if (category === 'social') {
      score += 0.5;
      reasoning.push('Full Moon increases visibility and social energy');
    }
    if (category === 'health') {
      score -= 0.5;
      reasoning.push('Full Moon can bring restlessness and poor sleep');
      badFor.push('rest', 'recovery');
    }
  } else if (moonPhase.phaseName.includes('Waning')) {
    if (category === 'decisions') {
      score -= 0.5;
      reasoning.push('Waning moon is not the time to initiate');
      badFor.push('starting new ventures');
    }
  } else if (moonPhase.phaseName.includes('Waxing')) {
    if (category === 'career') {
      score += 0.5;
      reasoning.push('Waxing moon builds career momentum');
      goodFor.push('pushing forward');
    }
  }

  // Clamp score to 1-10
  score = Math.max(1, Math.min(10, Math.round(score)));

  // Generate advice based on score
  let advice: string;
  if (score >= 9) {
    advice = 'The sky strongly supports you here. Act with confidence.';
    goodFor.push('taking action', 'making moves');
  } else if (score >= 7) {
    advice = 'Favorable conditions. Trust what feels right.';
    goodFor.push('steady progress', 'moving forward');
  } else if (score >= 5) {
    advice = 'Balanced ground. Stay aware, stay open.';
    goodFor.push('steady progress');
  } else if (score >= 3) {
    advice = 'Some resistance here. Patience serves you.';
    badFor.push('major decisions', 'rushing');
  } else {
    advice = 'Not the day for this. Redirect your attention.';
    badFor.push('big moves', 'confrontation');
    goodFor.push('reflection', 'planning');
  }

  // Deduplicate lists
  return {
    score,
    reasoning: [...new Set(reasoning)],
    advice,
    goodFor: [...new Set(goodFor)],
    badFor: [...new Set(badFor)],
  };
}

/**
 * Generate the complete personal daily report
 */
export function generatePersonalDailyReport(
  userProfile: UserProfile,
  date: Date = new Date()
): PersonalDailyReport {
  // Get current positions
  const currentPositions = calculateCurrentPositions(date);

  // Calculate transits to user's natal chart
  const transits = calculateTransits(userProfile.natalChart, currentPositions);

  // Get natal modifiers based on user's permanent chart placements
  const natalModifiers = getNatalModifiers(userProfile.natalChart);

  // Get moon phase
  const moonPhase = getMoonPhase(date);

  // Get day ruler (used for future headline generation)
  getDayRuler(date); // Called for side effects/validation

  // Calculate category scores - NOW INCLUDES NATAL MODIFIERS!
  const categories = {
    love: generateCategoryScore('love', transits, moonPhase, natalModifiers),
    career: generateCategoryScore('career', transits, moonPhase, natalModifiers),
    money: generateCategoryScore('money', transits, moonPhase, natalModifiers),
    health: generateCategoryScore('health', transits, moonPhase, natalModifiers),
    social: generateCategoryScore('social', transits, moonPhase, natalModifiers),
    decisions: generateCategoryScore('decisions', transits, moonPhase, natalModifiers),
    creativity: generateCategoryScore('creativity', transits, moonPhase, natalModifiers),
    spiritual: generateCategoryScore('spiritual', transits, moonPhase, natalModifiers)
  };

  // Calculate overall score (weighted average)
  const categoryScores = Object.values(categories).map(c => c.score);
  const overallScore = Math.round(
    categoryScores.reduce((a, b) => a + b, 0) / categoryScores.length
  );

  // Determine overall energy
  let overallEnergy: PersonalDailyReport['overallEnergy'];
  if (overallScore >= 8) overallEnergy = 'excellent';
  else if (overallScore >= 6) overallEnergy = 'good';
  else if (overallScore >= 4) overallEnergy = 'mixed';
  else if (overallScore >= 2) overallEnergy = 'challenging';
  else overallEnergy = 'difficult';

  // Generate headline based on strongest transit
  const strongestTransit = transits[0];
  let headline = 'The cosmos are in motion.';
  if (strongestTransit && strongestTransit.isExact) {
    headline = `Major energy: ${interpretTransit(strongestTransit)}`;
  }

  // Aspect angle/orb lookup for timing scans
  const ASPECT_ANGLES: Record<AspectType, { angle: number; maxOrb: number }> = {
    conjunction: { angle: 0, maxOrb: 8 },
    opposition: { angle: 180, maxOrb: 8 },
    trine: { angle: 120, maxOrb: 6 },
    square: { angle: 90, maxOrb: 6 },
    sextile: { angle: 60, maxOrb: 4 },
    quincunx: { angle: 150, maxOrb: 3 },
  };

  // Key transits (top 5 by orb)
  const keyTransits = transits.slice(0, 5).map((transit, index) => {
    const affectedCategories = [
      ...PLANET_CATEGORY_INFLUENCE[transit.transitPlanet] || [],
      ...PLANET_CATEGORY_INFLUENCE[transit.natalPlanet] || []
    ].filter((v, i, a) => a.indexOf(v) === i) as QuestionCategory[];

    let impact: 'positive' | 'negative' | 'neutral';
    if (transit.aspectType === 'conjunction') {
      const conj = getConjunctionImpact(transit.transitPlanet, transit.natalPlanet);
      impact = conj > 0 ? 'positive' : conj < 0 ? 'negative' : 'neutral';
    } else {
      impact = ASPECT_IMPACTS[transit.aspectType].nature;
    }

    // Compute timing for top 3 transits
    let timing: import('../types/userProfile').TransitTiming | undefined;
    if (index < 3) {
      const aspectInfo = ASPECT_ANGLES[transit.aspectType];
      if (aspectInfo) {
        const result = scanTransitTiming(
          transit.natalPosition,
          transit.transitPlanet,
          aspectInfo.angle,
          aspectInfo.maxOrb,
          transit.orb,
        );
        if (result) timing = result;
      }
    }

    return {
      transit,
      interpretation: interpretTransit(transit),
      affectedCategories,
      impact,
      timing,
    };
  });

  // Check for retrogrades
  const retrogradePlanets: Planet[] = ['mercury', 'venus', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto'];
  const retrogrades = retrogradePlanets
    .filter(planet => isRetrograde(planet, date))
    .map(planet => ({
      planet,
      advice: getRetrogradeAdvice(planet)
    }));

  return {
    date,
    userId: userProfile.id,
    overallScore,
    overallEnergy,
    headline,
    categories,
    keyTransits,
    moonPhase: {
      name: moonPhase.phaseName,
      advice: getMoonPhaseAdvice(moonPhase.phaseName)
    },
    retrogrades,
    generatedAt: new Date()
  };
}

/**
 * Ordinal suffix for house numbers (1st, 2nd, 3rd, etc.)
 */
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Get advice for a retrograde planet
 */
function getRetrogradeAdvice(planet: Planet): string {
  const advice: Record<Planet, string> = {
    mercury: 'Review communications, avoid signing contracts',
    venus: 'Reflect on relationships and values',
    mars: 'Channel energy inward, avoid conflicts',
    jupiter: 'Internal growth over external expansion',
    saturn: 'Reassess structures and responsibilities',
    uranus: 'Inner revolution before outer change',
    neptune: 'Deep spiritual reflection',
    pluto: 'Internal transformation in progress',
    sun: '', moon: '', ascendant: '', midheaven: '', northNode: '', chiron: ''
  };
  return advice[planet] || 'Reflect and review';
}

/**
 * Get advice for moon phase
 */
function getMoonPhaseAdvice(phaseName: string): string {
  const advice: Record<string, string> = {
    'New Moon': 'Set intentions, plant seeds, begin fresh',
    'Waxing Crescent': 'Take first steps, build momentum',
    'First Quarter': 'Take action, overcome obstacles',
    'Waxing Gibbous': 'Refine and adjust your approach',
    'Full Moon': 'Harvest results, celebrate, release',
    'Waning Gibbous': 'Share wisdom, express gratitude',
    'Last Quarter': 'Release what no longer serves',
    'Waning Crescent': 'Rest, reflect, prepare for renewal'
  };
  return advice[phaseName] || 'Flow with the lunar cycle';
}

/**
 * Get the score for a specific category from the daily report
 * This is what the 8-ball will use to answer questions
 */
export function getCategoryScoreFromReport(
  report: PersonalDailyReport,
  category: QuestionCategory
): CategoryScore {
  const categoryMap: Record<QuestionCategory, keyof PersonalDailyReport['categories']> = {
    love: 'love',
    career: 'career',
    money: 'money',
    health: 'health',
    social: 'social',
    decisions: 'decisions',
    creativity: 'creativity',
    spiritual: 'spiritual',
    communication: 'social',
    conflict: 'decisions',
    timing: 'decisions'
  };

  return report.categories[categoryMap[category]] || report.categories.decisions;
}
